import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@/services/api';
import { EventOut, EventOccurrence } from '@/types/api';
import Loader from '@/components/atoms/Loader';
import Button from '@/components/atoms/Button';
import CalendarHeader from '@/components/molecules/CalendarHeader/CalendarHeader';
import CalendarControls from '@/components/molecules/CalendarControls/CalendarControls';
import CalendarWeekdays from '@/components/molecules/CalendarWeekdays/CalendarWeekdays';
import EventCalendarGrid from '@/components/organisms/EventCalendarGrid/EventCalendarGrid';
import { CalendarOccurrence } from '@/components/organisms/EventCalendar/types';
import './EventCalendar.scss';

const EventCalendar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventOut[]>([]);
  const [calendarOccurrences, setCalendarOccurrences] = useState<CalendarOccurrence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(() => new Date());

  const monthStart = useMemo(() => {
    return new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  }, [currentMonth]);

  const monthEnd = useMemo(() => {
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [currentMonth]);

  const getErrorMessage = useCallback((err: any) => {
    return err.message || t('errors.general');
  }, [t]);

  const formatMonthLabel = useMemo(() => {
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'long',
      year: 'numeric'
    }).format(monthStart);
  }, [i18n.language, monthStart]);

  const parseLocalDateTime = useCallback((value: string) => {
    const normalized = value.replace(/Z$/, '').replace(/([+-]\d{2}:\d{2})$/, '');
    return new Date(normalized);
  }, []);

  const getRecurrenceWindowEnd = useCallback((event: EventOut, startValue: string) => {
    if (!event.recurrence_time_windows) return null;
    const start = parseLocalDateTime(startValue);
    if (Number.isNaN(start.getTime())) return null;
    const dayKey = String((start.getDay() + 6) % 7);
    const window = event.recurrence_time_windows[dayKey];
    if (!window || !window.start_time) return null;

    let end: Date | null = null;
    if (window.end_time) {
      const [endHour, endMinute] = window.end_time.split(':').map(Number);
      end = new Date(start);
      end.setHours(endHour || 0, endMinute || 0, 0, 0);
      if (end <= start) {
        end.setDate(end.getDate() + 1);
      }
    } else if (window.duration_hours && window.duration_hours > 0) {
      end = new Date(start);
      end.setHours(end.getHours() + window.duration_hours);
    }
    return end ? end.toISOString() : null;
  }, [parseLocalDateTime]);

  const buildCalendarOccurrences = useCallback(async (eventsData: EventOut[]) => {
    if (!Array.isArray(eventsData) || eventsData.length === 0) {
      setCalendarOccurrences([]);
      return;
    }

    const occurrences: CalendarOccurrence[] = [];
    const range = {
      start_date: monthStart.toISOString(),
      end_date: monthEnd.toISOString()
    };

    await Promise.all(
      eventsData.map(async event => {
        if (event.is_recurring) {
          try {
            const data = await apiService.getEventOccurrences(event.id, range);
            if (Array.isArray(data)) {
              data.forEach((occurrence: EventOccurrence) => {
                occurrences.push({
                  event,
                  start_date: occurrence.start_date,
                  end_date: occurrence.end_date
                    ?? getRecurrenceWindowEnd(event, occurrence.start_date)
                    ?? event.end_date
                });
              });
            }
          } catch (err) {
            console.warn(`Failed to fetch occurrences for event ${event.id}:`, err);
          }
        } else if (event.start_date) {
          const startDate = new Date(event.start_date);
          if (startDate >= monthStart && startDate <= monthEnd) {
            occurrences.push({
              event,
              start_date: event.start_date,
              end_date: event.end_date
            });
          }
        }
      })
    );

    occurrences.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    setCalendarOccurrences(occurrences);
  }, [getRecurrenceWindowEnd, monthStart, monthEnd]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const eventsData = await apiService.getAnonymousEvents();
        if (!Array.isArray(eventsData)) {
          setEvents([]);
          setCalendarOccurrences([]);
          return;
        }
        setEvents(eventsData);
      } catch (err: any) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [getErrorMessage]);

  useEffect(() => {
    if (events.length > 0) {
      buildCalendarOccurrences(events);
    } else {
      setCalendarOccurrences([]);
    }
  }, [events, buildCalendarOccurrences]);

  const occurrencesByDate = useMemo(() => {
    const map = new Map<string, CalendarOccurrence[]>();
    calendarOccurrences.forEach(item => {
      const key = item.start_date.slice(0, 10);
      const existing = map.get(key) || [];
      existing.push(item);
      map.set(key, existing);
    });
    return map;
  }, [calendarOccurrences]);

  const weekdayLabels = [
    t('events.calendar.weekdays.mon'),
    t('events.calendar.weekdays.tue'),
    t('events.calendar.weekdays.wed'),
    t('events.calendar.weekdays.thu'),
    t('events.calendar.weekdays.fri'),
    t('events.calendar.weekdays.sat'),
    t('events.calendar.weekdays.sun')
  ];

  const startOffset = (monthStart.getDay() + 6) % 7;
  const gridStartDate = useMemo(() => {
    const start = new Date(monthStart);
    start.setDate(monthStart.getDate() - startOffset);
    return start;
  }, [monthStart, startOffset]);

  const calendarCells = useMemo(() => {
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStartDate);
      date.setDate(gridStartDate.getDate() + index);
      const key = date.toISOString().slice(0, 10);
      return {
        date,
        key,
        isCurrentMonth: date.getMonth() === monthStart.getMonth(),
        occurrences: occurrencesByDate.get(key) || []
      };
    });
  }, [gridStartDate, monthStart, occurrencesByDate]);

  const formatTime = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return new Intl.DateTimeFormat(i18n.language, {
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsed);
  };

  if (loading) {
    return <Loader fullScreen size="lg" />;
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <p>{error}</p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="events-calendar-page">
        <CalendarHeader
          title={t('events.calendar.title')}
          onBack={() => navigate('/events')}
        />

        <CalendarControls
          monthLabel={formatMonthLabel}
          prevLabel={t('events.calendar.previous')}
          nextLabel={t('events.calendar.next')}
          onPrev={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
          onNext={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
        />

        <CalendarWeekdays labels={weekdayLabels} />

        {calendarOccurrences.length === 0 && (
          <div className="events-calendar-page__empty">
            {t('events.calendar.noEvents')}
          </div>
        )}

        <EventCalendarGrid
          cells={calendarCells}
          onSelectEvent={(eventId) => navigate(`/events/${eventId}/venues/0`)}
          formatTime={formatTime}
        />
      </div>
    </div>
  );
};

export default EventCalendar;
